import { useMemo, useState } from 'react'
import {
  ChannelIcon,
  ChevronDownIcon,
  EmptyState,
  SearchIcon,
  Sidebar,
  Skeleton,
  SkeletonText,
  Text,
  Link,
  useSidebar,
} from '@buffer-mono/popcorn'

import type { Channel, Service } from '~publish/gql/graphql'
import { useIsAdmin } from '~publish/contexts/AccountContext'
import { getChannelColor, getChannelName } from '~publish/helpers/channels'
import { useConnectChannel } from '~publish/hooks/useConnectChannel'
import { useCta } from '~publish/hooks/useCta'

import { filterChannels, getChannelSuggestions } from './helpers'
import { NewChannelButton } from './NewChannelButton'
import { ChannelNavItem } from './ChannelNavItem'
import { Service as ServiceEnum } from '~publish/legacy/constants/services/ServiceDefinitions'

import styles from './ChannelNav.module.css'
import { useCommentsConfig } from '~publish/pages/Comments/CommentsChannelConfigContext'
import { useChannelCommentCounts } from '~publish/pages/Comments/hooks/useChannelCommentCounts'
import { FeedbackWidget } from '~publish/components/FeedbackWidget'

interface ChannelNavProps {
  channels: Channel[]
  searching: boolean
  searchQuery: string
  hovered: boolean
}

export const ChannelNavSkeleton = (): JSX.Element => {
  return (
    <Sidebar.List>
      {Array.from({ length: 5 }).map((_, index) => (
        <Sidebar.ListItem key={index}>
          <Sidebar.ButtonSkeleton
            prefix={<Skeleton width={32} height={32} rounded />}
            size="large"
          >
            <SkeletonText lines={1} width={86} />
          </Sidebar.ButtonSkeleton>
        </Sidebar.ListItem>
      ))}
    </Sidebar.List>
  )
}

const FeedbackLink = (): JSX.Element => {
  return (
    <FeedbackWidget
      id="comments-sidebar-channel-1"
      source="comments"
      description="Which channels should we add to Community?"
    >
      <Link>Let us know</Link>
    </FeedbackWidget>
  )
}

export function ChannelNav({
  channels,
  searching,
  searchQuery,
  hovered,
}: ChannelNavProps): JSX.Element {
  const isAdmin = useIsAdmin()
  const { config } = useCommentsConfig()
  const { open } = useSidebar()

  let channelConnectionSuggestions = getChannelSuggestions(channels)
  const { counts } = useChannelCommentCounts({
    channels,
    status: ['unreplied'],
  })

  const displayedChannels = useMemo(() => {
    if (!searchQuery) {
      return []
    }
    return filterChannels(channels, searchQuery)
  }, [channels, searchQuery])

  const shouldShowCommentsChannelSuggestions =
    !config.hasSupportedCommentChannel(channels)

  if (shouldShowCommentsChannelSuggestions) {
    // @TODO add suggestion for all the Comments supported channels
    channelConnectionSuggestions =
      channelConnectionSuggestions?.filter(
        (service) => service !== ServiceEnum.Threads,
      ) ?? []
  }

  const supportedChannels = useMemo(
    () => channels.filter(config.isSupportedInComments),
    [channels, config.isSupportedInComments],
  )

  const getCounterForChannel = (channelId: string): number | null => {
    const channel = supportedChannels.find((c) => c.id === channelId)
    if (!channel) {
      return null
    }
    return (
      counts?.filter((count) => count.channelId === channel.id)[0]?.count ??
      null
    )
  }

  if (searching) {
    return (
      <>
        <Sidebar.List>
          {displayedChannels.map((channel: Channel) => (
            <ChannelNavItem
              channel={channel}
              key={channel.id}
              commentCounts={getCounterForChannel(channel.id)}
            />
          ))}
        </Sidebar.List>
        {!searchQuery && (
          <EmptyState size="small">
            <EmptyState.Description color="subtle">
              Start typing to search channels.
            </EmptyState.Description>
          </EmptyState>
        )}
        {searchQuery && displayedChannels.length === 0 && (
          <EmptyState size="small">
            <EmptyState.Icon>
              <SearchIcon />
            </EmptyState.Icon>
            <EmptyState.Heading>No channels found</EmptyState.Heading>
            <EmptyState.Description>
              Try searching for a different channel name.
            </EmptyState.Description>
          </EmptyState>
        )}
      </>
    )
  }

  return (
    <>
      <Sidebar.List>
        {/* TODO: We can add all channels item here once we are ready to support it */}
        {/* <AllChannelsItem /> */}
        {shouldShowCommentsChannelSuggestions && (
          <ChannelSuggestions services={[ServiceEnum.Threads]} />
        )}
        {channels.map((channel) => (
          <ChannelNavItem
            channel={channel}
            key={channel.id}
            commentCounts={getCounterForChannel(channel.id)}
          />
        ))}
      </Sidebar.List>
      {isAdmin && (
        <>
          {channelConnectionSuggestions && (
            <ChannelSuggestions services={channelConnectionSuggestions} />
          )}
          <Sidebar.List>
            <NewChannelButton />
            {(open || hovered) && (
              <Text color="subtle" className={styles.moreChannelsText}>
                More channels coming soon to Community. <FeedbackLink /> which
                ones we should add first.
              </Text>
            )}
          </Sidebar.List>
        </>
      )}
    </>
  )
}

const ChannelSuggestions = ({
  services,
}: {
  services: Service[]
}): JSX.Element => {
  const [showAll, setShowAll] = useState(false)

  const connectChannel = useConnectChannel({
    cta: 'publish-comments-sidebar-addChannelSuggestions-1',
  })

  const { clicked: ctaClicked } = useCta(
    'publish-comments-sidebar-addChannelSuggestions-1',
    {
      upgradePathName: 'addChannel-upgrade',
    },
  )

  const onChannelClick = (channelService: Service): void => {
    ctaClicked()
    connectChannel(channelService)
  }

  const displayServices = showAll ? services : services.slice(0, 3)
  const showMore = services.length > displayServices.length && !showAll

  return (
    <>
      <Sidebar.List>
        {displayServices.map((service: Service) => (
          <Sidebar.ListItem key={service}>
            <Sidebar.Button
              size="large"
              className={styles.channelSuggestion}
              style={
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                {
                  '--channel-color': getChannelColor(service),
                } as React.CSSProperties
              }
              prefix={
                <span className={styles.icon}>
                  {}
                  <ChannelIcon type={service} />
                </span>
              }
              onClick={(): void => onChannelClick(service)}
            >
              Connect {getChannelName(service)}
            </Sidebar.Button>
          </Sidebar.ListItem>
        ))}
      </Sidebar.List>
      {showMore ? (
        <Sidebar.List>
          <Sidebar.ListItem>
            <Sidebar.Button
              size="large"
              prefix={
                <span className={styles.icon}>
                  <ChevronDownIcon />
                </span>
              }
              className={styles.showMoreSuggestionsButton}
              onClick={(): void => setShowAll(!showAll)}
            >
              Show more channels
            </Sidebar.Button>
          </Sidebar.ListItem>
        </Sidebar.List>
      ) : null}
    </>
  )
}
